/* ⚠️ GENERATED from app/services/aiIdentity.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/aiIdentity.ts — who Allo AI is talking to, and who else is here.
//
// 🟥 IT KNEW ALMOST NONE OF THIS. The assistant was handed a `userName` in its
//    own chat and nothing at all when summoned with `@ai` in a conversation —
//    so in a group of five it had no idea whose question it was answering, and
//    in a one-to-one it could not use either person's name. "What do you think
//    of Abdullah's question" was a sentence about a stranger.
//
// 🟥 AND THE ONE NAME IT DID GET COULD BE AN EMAIL ADDRESS. The fallback chain
//    ended `|| currentUser.email`, so an account with no full name introduced
//    itself to a language model as `a.almatbaee@gmail.com` — which is not a
//    name, reads badly in a reply, and is a small piece of personal data handed
//    over for no reason at all.
//
// 🟢 SO THERE IS ONE ANSWER TO "WHAT IS THIS PERSON CALLED", and both clients
//    use it. A profile's full name is what people set for other people to read;
//    a username is a handle, and an email is an identifier. They are not
//    interchangeable and the order matters.

/**
 * What to call someone.
 *
 * 🟥 NEVER THE EMAIL. It is not a name — it is the one field here that is
 *    personal data rather than a label somebody chose to be known by. It is not
 *    a last resort either: an empty string, and a caller that supplies its own
 *    fallback, is better than sending an address to a model.
 *
 * 🟢 AND A `displayName` THAT IS AN EMAIL IS AN EMAIL. Firebase fills that
 *    field from the sign-in provider, which for an email sign-up is the address
 *    itself, so the rule has to look at what is in it rather than at its name.
 */
export function personName(p, fallback = '') {
  const pick = v => String(v || '').trim();
  const full = pick(p?.fullName) || pick(p?.name);
  if (full && !full.includes('@')) return full;
  const shown = pick(p?.displayName);
  if (shown && !shown.includes('@')) return shown;
  const handle = pick(p?.username);
  if (handle) return handle.replace(/^@+/, '');
  return fallback;
}
const list = names => {
  const clean = names.map(s => String(s || '').trim()).filter(Boolean);
  if (!clean.length) return '';
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
};

/**
 * The paragraph the model is given about the room it is answering in.
 *
 * 🟢 WRITTEN HERE, NOT ON THE SERVER, so the phone and the browser cannot
 *    describe the same conversation two different ways — and not at the two
 *    call sites either, so a group and a one-to-one cannot drift apart. The
 *    server's job is to paste it into the system prompt, nothing more.
 *
 * 🟥 AND IT ALWAYS SAYS WHO ASKED. In a group the answer is read by everybody,
 *    so "answer Abdullah" and "answer the group" are different instructions and
 *    the model has no way to guess which one it is looking at. Without that
 *    line it addresses whoever it saw last.
 *
 * 🟢 THE OTHERS ARE NAMED BUT NOT ADDRESSED. They are context — the assistant
 *    is answering one person in front of the others, which is exactly what it
 *    looks like in the chat.
 */
export function sceneFor(s) {
  const me = String(s?.me || '').trim();
  const who = me ? `You are talking to ${me}.` : '';
  if (s?.isAssistantChat) {
    /* 🟢 THE ASSISTANT'S OWN CHAT HAS NOBODY ELSE IN IT, and saying so stops the
       model hedging about who can read the reply. */
    return `${who} This is your private one-to-one chat with them; nobody else can read it.`.trim();
  }
  const others = list(s?.others || []);
  if (s?.isGroup) {
    const named = String(s?.groupName || '').trim();
    const where = named ? `the group "${named}"` : 'a group chat';
    return [who, `They asked you inside ${where}${others ? `, whose other members are ${others}` : ''}.`, 'Everyone in the group will read your answer, so address them by name and keep it useful to the room.'].filter(Boolean).join(' ');
  }
  return [who, others ? `They asked you inside their private chat with ${others}, who will also read your answer.` : 'They asked you inside a private chat.', 'Use the names naturally when it helps; do not list them back.'].filter(Boolean).join(' ');
}
